import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiLogOut, FiHome, FiExternalLink } from 'react-icons/fi';
import logoOctopus from '../../assets/images/logo.png';
import { useAuth } from '../../contexts/AuthContext';

// Importar iconos existentes
import clientesIcon from '../../assets/images/iconos/clientes.png';
import inventarioIcon from '../../assets/images/iconos/inventario.png';
import facturacionIcon from '../../assets/images/iconos/facturacion.png';
import proveedoresIcon from '../../assets/images/iconos/proveedores.png';
import productosIcon from '../../assets/images/iconos/productos.png';
import informesIcon from '../../assets/images/iconos/informes.png';
import ventasEstimadosIcon from '../../assets/images/iconos/ventas_estimados.png';
import homeIcon from '../../assets/images/iconos/home.png';
import vendedoresIcon from '../../assets/images/iconos/vendedores.png';
import formulacionesIcon from '../../assets/images/iconos/formulaciones.png';
import ordenes_compraIcon from '../../assets/images/iconos/ordenes_compra.png';
import procesosIcon from '../../assets/images/iconos/procesos.png';
import seguimientoIcon from '../../assets/images/iconos/seguimiento.png';
import administracionIcon from '../../assets/images/iconos/administracion.png';
import portalIcon from '../../assets/images/iconos/portal.png';

const Sidebar = ({ isOpen, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, hasPermission } = useAuth();

  // Cada módulo declara el permiso RBAC requerido para mostrarse.
  // `permission` puede ser un string o un array (any-of). Sin `permission` => siempre visible.
  const modules = [
    {
      path: '/system',
      label: 'HOME',
      icon: homeIcon
    },
    {
      path: '/system/ventas',
      label: 'TOMA DE ÓRDENES Y COTIZACIONES',
      icon: ventasEstimadosIcon,
      permission: 'ventas.view'
    },
    {
      path: '/system/facturacion',
      label: 'FACTURACIÓN',
      icon: facturacionIcon,
      permission: 'ventas.invoices'
    },
    {
      path: '/system/clientes',
      label: 'CLIENTES',
      icon: clientesIcon,
      permission: 'clientes.view'
    },
    {
      path: '/system/productos',
      label: 'PRODUCTOS',
      icon: productosIcon,
      permission: 'productos.view'
    },
    {
      path: '/system/inventario',
      label: 'INVENTARIO',
      icon: inventarioIcon,
      permission: 'inventario.view'
    },
    {
      path: '/system/proveedores',
      label: 'PROVEEDORES',
      icon: proveedoresIcon,
      permission: 'proveedores.view'
    },
    {
      path: '/system/compras',
      label: 'ÓRDENES DE COMPRA',
      icon: ordenes_compraIcon
      // Sin permiso RBAC equivalente (no existe módulo COMPRAS en el seed)
    },
    {
      path: '/system/formulaciones',
      label: 'FORMULACIONES',
      icon: formulacionesIcon,
      permission: 'productos.formulas'
    },
    {
      path: '/system/seguimiento',
      label: 'SEGUIMIENTO DE NUEVOS PROSPECTOS',
      icon: seguimientoIcon,
      permission: 'seguimiento.view'
    },
    {
      path: '/system/vendedores',
      label: 'VENDEDORES DE BETEL',
      icon: vendedoresIcon,
      permission: 'vendedores.view'
    },
    {
      path: '/portal',
      label: 'PORTAL DEL CLIENTE',
      icon: portalIcon,
      external: true
    },
    {
      path: '/system/procesos',
      label: 'REGISTRO DE PROCESOS',
      icon: procesosIcon,
      permission: 'produccion.view'
    },
    {
      path: '/system/reportes',
      label: 'REPORTES GENERALES',
      icon: informesIcon,
      permission: 'reportes.view'
    },
    {
      path: '/system/admin',
      label: 'ADMINISTRADORES',
      icon: administracionIcon,
      permission: ['admin.users', 'admin.roles', 'admin.permissions', 'admin.settings']
    }
  ];

  // Filtrar módulos según los permisos RBAC del usuario
  const visibleModules = modules.filter(module => {
    // Si el módulo no requiere permisos, mostrarlo
    if (!module.permission) return true;
    // Array => basta con tener alguno (any-of); string => ese permiso exacto
    if (Array.isArray(module.permission)) {
      return module.permission.some(p => hasPermission(p));
    }
    return hasPermission(module.permission);
  });

  const isActive = (path) => {
    if (path === '/system') {
      return location.pathname === '/system' || location.pathname === '/system/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <div className={`sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar__header">
          <div className="logo">
            <img src={logoOctopus} alt="Betel Natural" />
          </div>
          <div className="menu-title">Modulos</div>
        </div>

        <nav className="sidebar__nav">
          <ul className="nav-list">
            {visibleModules.map((module) => {
              const iconElement = <img src={module.icon} alt={module.label} className="nav-icon" style={{ textDecoration: 'none' }} />;

              const labelElement = (
                <span className="nav-label" style={{ textDecoration: 'none' }}>
                  {module.label}
                  {module.external && <FiExternalLink style={{ marginLeft: '6px', fontSize: '0.75rem', opacity: 0.7 }} />}
                </span>
              );

              return (
                <li key={module.path} className="nav-item">
                  {module.external ? (
                    <a
                      href={module.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="nav-link"
                      style={{ textDecoration: 'none' }}
                      onClick={() => {
                        if (window.innerWidth <= 767) {
                          onToggle();
                        }
                      }}
                    >
                      {iconElement}
                      {labelElement}
                    </a>
                  ) : (
                    <Link
                      to={module.path}
                      className={`nav-link ${isActive(module.path) ? 'active' : ''}`}
                      style={{ textDecoration: 'none' }}
                      onClick={() => {
                        if (window.innerWidth <= 767) {
                          onToggle();
                        }
                      }}
                    >
                      {iconElement}
                      {labelElement}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="sidebar__footer">
          <button 
            className="btn btn--ghost btn--small logout-button"
            onClick={() => {
              logout();
              navigate('/login');
            }}
            title="Cerrar Sesión"
          >
            <FiLogOut className="logout-icon" />
            <span className="logout-text">Cerrar Sesión</span>
          </button>         
        </div>
      </div>

      {/* Overlay para mobile */}
      <div 
        className={`mobile-overlay ${isOpen ? 'show' : ''}`}
        onClick={onToggle}
      />
    </>
  );
};

export default Sidebar;